import { useEffect, useState, type ReactNode } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import { formatLabel, toIsoUtc } from "../labels";
import type {
  DataClassification,
  NotificationChannel,
  NotificationType,
} from "../types";

const NOTIFICATION_TYPES: NotificationType[] = [
  "marketing",
  "operational",
  "system",
  "policy",
  "payment",
  "booking",
  "account_status",
  "enforcement",
  "incident",
];

const CHANNELS: NotificationChannel[] = [
  "in_app",
  "push",
  "email",
  "future_governed_channel",
];

const CLASSIFICATIONS: DataClassification[] = [
  "public",
  "internal",
  "confidential",
  "strictly_confidential",
  "regulated",
];

export function ConfirmActionDialog({
  open,
  title,
  notice,
  actions,
  requireReason = true,
  requireApproval = false,
  approvalLabel = "Approval ID (when required)",
  extraFields,
  confirmVariant = "primary",
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  notice?: string;
  actions: string[];
  requireReason?: boolean;
  requireApproval?: boolean;
  approvalLabel?: string;
  extraFields?: ReactNode;
  confirmVariant?: "primary" | "danger";
  onClose: () => void;
  onSubmit: (input: {
    action: string;
    reason?: string;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const first = actions[0] ?? "";
  const [action, setAction] = useState(first);
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (!open) return;
    setAction(first);
    setReason("");
    setApprovalId("");
  }, [open, first]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            loading={busy}
            disabled={
              !action ||
              (requireReason && reason.trim().length < 3) ||
              (requireApproval && approvalId.trim().length === 0)
            }
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(approvalId.trim()
                  ? { approval_id: approvalId.trim() }
                  : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          options={actions.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {extraFields}
        <Textarea
          label={requireReason ? "Reason / rationale" : "Reason (optional)"}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <ApprovalPickerField
          label={approvalLabel}
          value={approvalId}
          onChange={setApprovalId}
          required={requireApproval}
        />
        {error ? (
          <Alert tone="danger" title="Action failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateCampaignDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [channel, setChannel] = useState<NotificationChannel>("in_app");
  const [templateId, setTemplateId] = useState("");
  const [audienceId, setAudienceId] = useState("");
  const [recipientUserId, setRecipientUserId] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [sourceId, setSourceId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [highReach, setHighReach] = useState(false);
  const [isSensitive, setIsSensitive] = useState(true);
  const [companyWide, setCompanyWide] = useState(false);
  const [approvalId, setApprovalId] = useState("");

  useEffect(() => {
    if (!open) return;
    setChannel("in_app");
    setTemplateId("");
    setAudienceId("");
    setRecipientUserId("");
    setSourceType("");
    setSourceId("");
    setScheduledAt("");
    setHighReach(false);
    setIsSensitive(true);
    setCompanyWide(false);
    setApprovalId("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create marketing campaign (draft)"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!audienceId.trim() && !recipientUserId.trim()}
            onClick={() => {
              const body: Record<string, unknown> = {
                notification_type: "marketing",
                channel,
                is_sensitive: isSensitive,
                high_reach: highReach,
                company_wide: companyWide,
                source_workflow_id: "WF-011",
              };
              if (templateId.trim()) {
                body.notification_template_id = templateId.trim();
              }
              if (audienceId.trim()) body.audience_id = audienceId.trim();
              if (recipientUserId.trim()) {
                body.recipient_user_id = recipientUserId.trim();
              }
              if (sourceType.trim() && sourceId.trim()) {
                body.source_type = sourceType.trim();
                body.source_id = sourceId.trim();
              }
              if (scheduledAt.trim()) {
                body.scheduled_at = toIsoUtc(scheduledAt);
              }
              if (approvalId.trim()) body.approval_id = approvalId.trim();
              void onSubmit(body);
            }}
          >
            Create draft
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Creates a draft notification via the HQ Notifications API. This does
          not send messages, prepare deliveries, or activate fan-out. Default
          type is marketing; mark sensitive when content warrants Manager
          Approval.
        </Text>
        <Select
          label="Channel"
          value={channel}
          onChange={(event) =>
            setChannel(event.target.value as NotificationChannel)
          }
          options={CHANNELS.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Input
          label="Template ID (optional)"
          value={templateId}
          onChange={(event) => setTemplateId(event.target.value)}
        />
        <Input
          label="Audience ID"
          value={audienceId}
          onChange={(event) => setAudienceId(event.target.value)}
          hint="Provide audience_id and/or recipient_user_id (API requires one)."
        />
        <Input
          label="Recipient User ID"
          value={recipientUserId}
          onChange={(event) => setRecipientUserId(event.target.value)}
        />
        <Input
          label="Source type (optional)"
          value={sourceType}
          onChange={(event) => setSourceType(event.target.value)}
        />
        <Input
          label="Source ID (optional UUID)"
          value={sourceId}
          onChange={(event) => setSourceId(event.target.value)}
        />
        <Input
          label="Scheduled at (optional)"
          type="datetime-local"
          value={scheduledAt}
          onChange={(event) => setScheduledAt(event.target.value)}
        />
        <Checkbox
          label="Sensitive content"
          checked={isSensitive}
          onChange={(event) => setIsSensitive(event.target.checked)}
        />
        <Checkbox
          label="High reach"
          checked={highReach}
          onChange={(event) => setHighReach(event.target.checked)}
        />
        <Checkbox
          label="Company-wide (Dual Approval)"
          checked={companyWide}
          onChange={(event) => setCompanyWide(event.target.checked)}
        />
        <ApprovalPickerField
          label="Approved Approval (when required)"
          value={approvalId}
          onChange={setApprovalId}
          minAuthority={companyWide ? "dual" : undefined}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateTemplateDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [name, setName] = useState("");
  const [notificationType, setNotificationType] =
    useState<NotificationType>("marketing");
  const [channel, setChannel] = useState<NotificationChannel>("in_app");
  const [classification, setClassification] =
    useState<DataClassification>("internal");
  const [contentReference, setContentReference] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");

  useEffect(() => {
    if (!open) return;
    setName("");
    setNotificationType("marketing");
    setChannel("in_app");
    setClassification("internal");
    setContentReference("");
    setOwnerUserId("");
    setEffectiveFrom("");
    setEffectiveTo("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create notification template"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!name.trim() || !contentReference.trim()}
            onClick={() => {
              const body: Record<string, unknown> = {
                name: name.trim(),
                notification_type: notificationType,
                channel,
                classification,
                content_reference: contentReference.trim(),
              };
              if (ownerUserId.trim()) {
                body.owner_user_id = ownerUserId.trim();
              }
              if (effectiveFrom.trim()) {
                body.effective_from = toIsoUtc(effectiveFrom);
              }
              if (effectiveTo.trim()) {
                body.effective_to = toIsoUtc(effectiveTo);
              }
              void onSubmit(body);
            }}
          >
            Create draft
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Templates are draft until submitted and approved. content_reference
          points to governed copy — this screen does not edit live message
          bodies or send mail.
        </Text>
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Select
          label="Notification type"
          value={notificationType}
          onChange={(event) =>
            setNotificationType(event.target.value as NotificationType)
          }
          options={NOTIFICATION_TYPES.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Select
          label="Channel"
          value={channel}
          onChange={(event) =>
            setChannel(event.target.value as NotificationChannel)
          }
          options={CHANNELS.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Select
          label="Classification"
          value={classification}
          onChange={(event) =>
            setClassification(event.target.value as DataClassification)
          }
          options={CLASSIFICATIONS.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Textarea
          label="Content reference"
          value={contentReference}
          onChange={(event) => setContentReference(event.target.value)}
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        <Input
          label="Effective from (optional)"
          type="datetime-local"
          value={effectiveFrom}
          onChange={(event) => setEffectiveFrom(event.target.value)}
        />
        <Input
          label="Effective to (optional)"
          type="datetime-local"
          value={effectiveTo}
          onChange={(event) => setEffectiveTo(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CreateAudienceDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [classification, setClassification] =
    useState<DataClassification>("confidential");
  const [criteriaJson, setCriteriaJson] = useState("{}");
  const [criteriaError, setCriteriaError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setPurpose("");
    setOwnerUserId("");
    setClassification("confidential");
    setCriteriaJson("{}");
    setCriteriaError(null);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create audience (draft)"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!name.trim()}
            onClick={() => {
              let criteria: Record<string, unknown> = {};
              try {
                const parsed: unknown = JSON.parse(criteriaJson || "{}");
                if (
                  typeof parsed !== "object" ||
                  parsed === null ||
                  Array.isArray(parsed)
                ) {
                  setCriteriaError("criteria_definition must be a JSON object");
                  return;
                }
                criteria = parsed as Record<string, unknown>;
                setCriteriaError(null);
              } catch {
                setCriteriaError("Invalid JSON for criteria_definition");
                return;
              }
              const body: Record<string, unknown> = {
                name: name.trim(),
                classification,
                criteria_definition: criteria,
              };
              if (purpose.trim()) body.purpose = purpose.trim();
              if (ownerUserId.trim()) body.owner_user_id = ownerUserId.trim();
              void onSubmit(body);
            }}
          >
            Create draft
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Audience definitions are governed records. This UI does not invent
          automation rules, consent logic, or bulk CSV upload. Membership is
          added one user at a time with confirmation.
        </Text>
        <Input
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <Textarea
          label="Purpose (optional)"
          value={purpose}
          onChange={(event) => setPurpose(event.target.value)}
        />
        <Input
          label="Owner User ID (optional)"
          value={ownerUserId}
          onChange={(event) => setOwnerUserId(event.target.value)}
        />
        <Select
          label="Classification"
          value={classification}
          onChange={(event) =>
            setClassification(event.target.value as DataClassification)
          }
          options={CLASSIFICATIONS.map((value) => ({
            value,
            label: formatLabel(value),
          }))}
        />
        <Textarea
          label="criteria_definition (JSON object)"
          value={criteriaJson}
          onChange={(event) => setCriteriaJson(event.target.value)}
          hint="Stored as opaque JSON per API contract — not evaluated here."
        />
        {criteriaError ? (
          <Alert tone="danger" title="Invalid criteria">
            {criteriaError}
          </Alert>
        ) : null}
        {error ? (
          <Alert tone="danger" title="Create failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function AddMembershipDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { user_id: string; reason?: string | null }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setUserId("");
    setReason("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add audience membership"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!userId.trim()}
            onClick={() =>
              void onSubmit({
                user_id: userId.trim(),
                ...(reason.trim() ? { reason: reason.trim() } : {}),
              })
            }
          >
            Add member
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Single-user membership only. No bulk recipient upload. Confirm the
          User ID before submitting — audience data is sensitive.
        </Text>
        <Input
          label="User ID"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
        />
        <Textarea
          label="Reason (optional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? (
          <Alert tone="danger" title="Add failed">
            {error}
          </Alert>
        ) : null}
      </Stack>
    </Dialog>
  );
}

export function CampaignScheduleFields({
  scheduledAt,
  onChange,
  highReach,
  onHighReach,
  isSensitive,
  onSensitive,
  companyWide,
  onCompanyWide,
}: {
  scheduledAt: string;
  onChange: (value: string) => void;
  highReach: boolean;
  onHighReach: (value: boolean) => void;
  isSensitive: boolean;
  onSensitive: (value: boolean) => void;
  companyWide: boolean;
  onCompanyWide: (value: boolean) => void;
}) {
  return (
    <Stack gap={12}>
      <Input
        label="Scheduled at (required for schedule)"
        type="datetime-local"
        value={scheduledAt}
        onChange={(event) => onChange(event.target.value)}
      />
      <Checkbox
        label="Sensitive"
        checked={isSensitive}
        onChange={(event) => onSensitive(event.target.checked)}
      />
      <Checkbox
        label="High reach"
        checked={highReach}
        onChange={(event) => onHighReach(event.target.checked)}
      />
      <Checkbox
        label="Company-wide"
        checked={companyWide}
        onChange={(event) => onCompanyWide(event.target.checked)}
      />
    </Stack>
  );
}
